import React, { useEffect, useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import confetti from "canvas-confetti";

import {
  PackageCheck,
  Truck,
  MapPin,
  CreditCard,
  ClipboardList,
  ShoppingBag,
  BadgeCheck,
} from "lucide-react";

import "./OrderPlaced.css";
import Navbar from "./Navbar";
import Footer from "./Footer";

const OrderPlaced = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // ✅ NEW STRUCTURE: location.state.order
  const order = location.state?.order;

  const {
    orderId,
    paymentId,
    paymentMethod,
    couponDiscount,
    amount = {},
    items = [],
    address = {},
    promo,
    createdAt,
  } = order || {};

  const deliveryAddress = address?.delivery || {};
  const billingAddress = address?.billing || {};

  // ✅ Redirect if no order
  useEffect(() => {
    if (!order) {
      navigate("/my-profile?tab=orders", { replace: true });
    }
  }, [order, navigate]);

  // ✅ Confetti
  useEffect(() => {
    if (orderId) {
      confetti({
        particleCount: 150,
        spread: 80,
        origin: { y: 0.6 },
      });
    }
  }, [orderId]);

  // ✅ Delivery date from createdAt (fallback to now)
  const deliveryDate = useMemo(() => {
    const date = new Date(createdAt || Date.now());
    date.setDate(date.getDate() + 4);
    return date;
  }, [createdAt]);


  const steps = [
    { icon: <BadgeCheck />, label: "Placed", active: true },
    { icon: <PackageCheck />, label: "Packed", active: false },
    { icon: <Truck />, label: "Shipped", active: false },
    // { icon: <Truck />, label: "Out for Delivery", active: false },
    { icon: <BadgeCheck />, label: "Delivered", active: false },
  ];

  return (
    <div className="op-page">
      {/* <Navbar /> */}

      <div className="op-container">

        {/* HEADER */}
        <div className="op-success">
          <BadgeCheck size={42} className="op-success-icon" />
          <h1>Order Placed Successfully</h1>

          <p>
            Order ID: <b>#{orderId}</b>
          </p>

          {paymentId && (
            <p>
              Payment ID: <b>{paymentId}</b>
            </p>
          )}
        </div>

        {/* GRID */}
        <div className="op-grid">

          {/* LEFT */}
          <div className="op-card">

            <div className="op-title">
              <ClipboardList />
              Order Summary
            </div>

            <div className="op-row">
              <span>Items Total</span>
              <b>₹{amount?.itemsTotal?.toLocaleString()}</b>
            </div>

            <div className="op-row">
              <span>Shipping</span>
              <b
                style={{
                  color: amount?.isShippingFree ? "green" : "black",
                  textDecoration: amount?.isShippingFree ? "line-through" : "none",
                }}
              >
                ₹{amount?.shipping}
                {amount?.isShippingFree && (
                  <span style={{ marginLeft: "8px", color: "green" }}>FREE</span>
                )}
              </b>
            </div>

            {amount?.couponDiscount > 0 && (
              <div className="op-row">
                <span>Coupon Discount</span>
                <b>- ₹{amount?.discount}</b>
              </div>
            )}

            {/* {amount?.couponDiscount > 0 && (
              <div className="op-row">
                <span>Tax benifit on Coupon</span>
                <b>- ₹{amount?.couponDiscount * 0.18}</b>
              </div>
            )} */}

            {/* <div className="op-row">
              <span>Tax</span>
              <b>₹{amount?.tax}</b>
            </div> */}

            {promo?.code && (
              <div className="op-row">
                <span>Coupon</span>
                <b>{promo.code} (-₹{promo.discount})</b>
              </div>
            )}

            <div className="op-row">
              <span>Total Amount</span>
              <b>₹{amount?.total?.toLocaleString()}</b>
            </div>

            <div className="op-row">
              <span>Payment</span>
              <b>{paymentMethod}</b>
            </div>

            <div className="op-row">
              <span>Delivery</span>
              <b>{deliveryDate.toDateString()}</b>
            </div>

            <div className="op-items">
              {items?.slice(0, 3).map((item, i) => (
                <div key={i} className="op-item">
                  <ShoppingBag size={16} />
                  <span>{item.name}</span>
                  <small>x{item.quantity}</small>
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT */}
          <div className="op-card">

            <div className="op-title">
              <MapPin />
              Delivery Address
            </div>

            <p className="op-address">
              {deliveryAddress?.addressLineOne || "-"},{" "}
              {/* {deliveryAddress?.addressLineTwo || "-"},{" "} */}
              {deliveryAddress?.city || "-"},{" "}
              {deliveryAddress?.state || "-"} -{" "}
              {deliveryAddress?.pincode || "-"}
              {" "}, India
            </p>

            <div className="op-title">
              <MapPin />
              Billing Address
            </div>

            <p className="op-address">
              {billingAddress?.addressLineOne || "-"},{" "}
              {/* {deliveryAddress?.addressLineTwo || "-"},{" "} */}
              {billingAddress?.city || "-"},{" "}
              {billingAddress?.state || "-"} -{" "}
              {billingAddress?.pincode || "-"}
              {" "}, India
            </p>

            {/* <div className="op-note">
              📦 SMS + Email updates enabled
            </div> */}

            <div className="op-payment">
              <CreditCard size={18} />
              Payment Confirmed
            </div>
          </div>
        </div>

        {/* TIMELINE */}
        <div className="op-timeline">
          <h3>Order Tracking</h3>

          <div className="op-track">
            {steps.map((s, i) => (
              <div key={i} className={`op-step ${s.active ? "active" : ""}`}>
                <div className="icon">{s.icon}</div>
                <span>{s.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* ACTIONS */}
        <div className="op-actions">
          {/* <Link to={`/order/${orderId}`} className="btn primary">
            Track Order
          </Link> */}

          <Link to="/products" className="btn secondary">
            Continue Shopping
          </Link>

          <Link to="/my-profile?tab=orders" className="btn outline">
            View Orders
          </Link>
        </div>

        {/* TRUST */}
        <div className="op-trust">
          🔒 Secure Payment • 🚚 Fast Delivery • 🔁 Easy Returns • 📞 24/7 Support
        </div>

      </div>

      {/* <Footer /> */}
    </div>
  );
};

export default OrderPlaced;